import type { Maybe } from '@graphql-tools/utils'
import type { AxiosError } from 'axios'
import DataLoader from 'dataloader'

// import { GraphQLError } from 'graphql'
import type {
  CdmGetMortgageRequest,
  CdmGetMortgageResponse,
  CdmGetMortgageVersionResponse,
  CdmMortgage,
} from '../service/luther/model'
import { createBatchLoaderFn, createBatchLoaderFnWithReducer, getFirstUsedKey } from '../utils/dataloader'

const fetchMortgageBatchFn = createBatchLoaderFnWithReducer<CdmGetMortgageRequest, CdmGetMortgageResponse>(
  '/mortgage',
  {
    reduceKeys: ['mortgage_ids', 'filter_ext_ids', 'filter_property_secured_id', 'filter_case_id'],
    defaultParams: {
      mortgage_details: true,
    },
  },
)

type BatchKeyToFieldNameType = {
  [key in keyof CdmGetMortgageRequest]?: (key: string) => (mortgage: CdmMortgage) => boolean | undefined
}

const batchKeyToFieldName: BatchKeyToFieldNameType = {
  mortgage_ids: (key: string) => (mortgage: CdmMortgage) => mortgage?.mortgage_id === key,
  filter_ext_ids: (key: string) => (mortgage: CdmMortgage) =>
    !!mortgage?.external?.find((externalSource) => externalSource?.id === key),
  filter_property_secured_id: (key: string) => (mortgage: CdmMortgage) => mortgage?.property_secured_ids?.includes(key),
  filter_case_id: (key: string) => (mortgage: CdmMortgage) => mortgage?.case_ids?.includes(key),
}

// Batch loader function to fetch mortgage data at a specific version.
// Accepts an array of objects with mortgage_id, version, and optional show_history.
// Constructs the endpoint URL dynamically based on the presence of show_history.
const fetchMortgageAtVersionBatchFn = createBatchLoaderFn<
  { mortgage_id: string; version: number; show_history?: Maybe<number> },
  CdmGetMortgageVersionResponse
>(
  ({ version, mortgage_id, show_history }) =>
    show_history
      ? // If show_history is provided, append it as a query parameter.
        `/mortgage/${mortgage_id}/version/${version}?show_history=${show_history}`
      : // Otherwise, use the base version endpoint.
        `/mortgage/${mortgage_id}/version/${version}`,
  {
    forwardParams: false,
  },
)

export const createMortgageLoader = () =>
  new DataLoader(
    async (params: CdmGetMortgageRequest[]) => {
      const response = await fetchMortgageBatchFn(params)

      if (!response.mortgages) {
        return params.map(
          () => null,
          // new GraphQLError(`No mortgage found for query: ${JSON.stringify(param)}`, {
          //   extensions: { code: 'NOT_FOUND', payload: param, expection: response.exception },
          // })
        )
      }

      const batchKey = getFirstUsedKey(params[0], [
        'mortgage_ids',
        'filter_ext_ids',
        'filter_property_secured_id',
        'filter_case_id',
      ])

      if (!batchKey) {
        // If no batchKey found, return nulls
        return params.map(() => null)
      }

      return params.map((param) => {
        const keys = param[batchKey]
        if (!keys) return null

        // keys can be string or array
        const key = Array.isArray(keys) ? keys[0] : keys
        if (!key) return null

        const findFunction = batchKeyToFieldName[batchKey]?.(key as string)
        if (!findFunction) return null

        if (batchKey.startsWith('filter')) {
          // For filter keys, return all matches (could be empty array)
          const filtered = response.mortgages?.filter(findFunction) as CdmMortgage[]
          return filtered && filtered.length > 0 ? filtered : null
        }

        // For id keys, return the first match or null
        const mortgage = response.mortgages?.find(findFunction)
        return mortgage || null
      })
    },
    {
      cacheKeyFn: (params) => JSON.stringify(params),
    },
  )

// Creates a DataLoader for fetching mortgage data at a specific version.
// Accepts an array of objects each containing mortgage_id, version, and optional show_history.
// Uses fetchMortgageAtVersionBatchFn to batch and fetch the data.
// Maps each input param to its corresponding mortgage version response or error.
export const createMortgageVersionLoader = () =>
  new DataLoader(
    async (params: { mortgage_id: string; version: number; show_history?: Maybe<number> }[]) => {
      // Batch fetch all requested mortgage versions
      const responses = await fetchMortgageAtVersionBatchFn(params)

      // For each input param, find the corresponding response by BOTH mortgage_id and version.
      // Previously we matched only by mortgage_id which caused collisions when the same
      // mortgage_id was requested for multiple different versions within the same batch.
      return params.map((param) => {
        // Prefer an exact match on mortgage_id + version
        let responseOrError = responses.find((response) => {
          const m = (response as CdmGetMortgageVersionResponse).mortgage
          return !!m && m.mortgage_id === param.mortgage_id && m.version === param.version
        })

        // Fallback: if no exact match, fall back to matching by mortgage_id only
        if (!responseOrError) {
          responseOrError = responses.find(
            (response) => (response as CdmGetMortgageVersionResponse).mortgage?.mortgage_id === param.mortgage_id,
          )
          if (responseOrError) {
            console.warn(
              '[mortgageVersionLoader] partial match for mortgage_id; expected version',
              param.version,
              'falling back to returned version',
              (responseOrError as CdmGetMortgageVersionResponse).mortgage?.version,
            )
          }
        }

        // Return the mortgage object if found, otherwise return the error
        return (responseOrError as CdmGetMortgageVersionResponse)?.mortgage || (responseOrError as AxiosError<any>)
      })
    },
    {
      // Use JSON stringified params as the cache key for DataLoader
      cacheKeyFn: JSON.stringify,
    },
  )

export const mortgageLoader = createMortgageLoader()
export const mortgageVersionLoader = createMortgageVersionLoader()
